import React, { useEffect, useState } from 'react';
import {
  selectRegisterRowMoney,
  selectRegisterRowsForDoctor,
  selectBackdatedTag,
  doctorLabel,
} from './monthlyReportModel';
import { DISCHARGE_TYPE_LABELS, ADMISSION_STATUS_LABELS, PAYER_TYPE_LABELS } from '../../constants/admission';

const PAGE_SIZE = 50;

// No formatted value renders here for a real zero — a blank cell IS the
// "we could not compute this" signal (F4 spec). Never `formatPaise(0)`.
const BLANK = '—';

/**
 * DischargeRegister (F4 — IPD-MVP discharge-mis, BUILD-PLAN fast-slice-v2
 * §8 WAVE 2, binding to §7.2 FROZEN contract). The accountant's month-wise
 * discharge register: S2's minimal list (name/IP/dates/LOS/type) hardened
 * into the full register with money, pagination and the unreconciled flag.
 *
 * - Money reads ONLY through `monthlyReportModel.selectRegisterRowMoney` —
 *   never a local `formatPaise(row.x)` — so the register's column totals
 *   equal §7.1's `ipd` block via the SAME formatter (§7.2 binding rule).
 * - `reconciled === false` rows render every money cell BLANK ("—"), never
 *   a real ₹0 — the wire contract says money is 0 on those rows, but an
 *   accountant must never read that as "confirmed zero" (§8 F4 scope).
 *   The row itself is visually flagged (tinted + a stated badge).
 * - Client-side pagination at 50/page with a "Showing a–b of N" line (R10
 *   long-list state) — the whole `rows` array is fetched once; only the
 *   rendered slice changes.
 * - `discharge_type` renders via the shared `DISCHARGE_TYPE_LABELS`
 *   constant; a null type (legacy/TRANSFERRED_OUT rows) falls back to
 *   `ADMISSION_STATUS_LABELS[admission_status]` — never an inline literal
 *   for either.
 * - NO drill-in: the accountant holds no `VIEW_ADMISSION`. Rows carry no
 *   click handler, no link, no button — nothing to click into.
 * - NO ward/bed column: both attributes are deleted from the admission row
 *   at discharge-complete, so the contract omits them structurally ([X3]).
 *
 * DOCTOR-REPORTS P1 (FE-3) — additive optional props:
 *   - `doctorKey` — a `doctorKey()`-shaped filter key, or null for all rows.
 *   - `doctorLabelText` — the selected doctor's label, for the filtered
 *     empty-state sentence.
 *   - `showPaidAfterDischarge` (default true) — hides the "Paid after
 *     discharge" column when the org month term is zero (D3), driven by the
 *     page via `selectShowPaidAfterDischarge`.
 * A "Doctor" column sits directly after "Patient", always filled (never
 * blank) via the shared `doctorLabel` selector.
 *
 * DOCTOR-REPORTS P1 scope-add — a "Concession" column sits directly after
 * "Net billed" (memo only, already netted into `net_billed_paise`; a header
 * tooltip says so). Reads `row.concessions_paise` via `selectRegisterRowMoney`
 * — same BLANK-on-unreconciled convention as every other money cell here.
 */
export default function DischargeRegister({
  register, loading, error, doctorKey = null, doctorLabelText = null, showPaidAfterDischarge = true,
}) {
  const allRows = register?.rows || [];
  const rows = selectRegisterRowsForDoctor(allRows, doctorKey);
  const periodLabel = register?.period?.label;
  const hasUnreconciledRow = rows.some((r) => !r.reconciled);
  const [page, setPage] = useState(0);

  // New fetch (new `register` object) OR a doctor-filter change resets to
  // page 1 — never carries a stale page index into a different row count.
  useEffect(() => {
    setPage(0);
  }, [register, doctorKey]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const start = currentPage * PAGE_SIZE;
  const end = Math.min(start + PAGE_SIZE, rows.length);
  const pageRows = rows.slice(start, end);

  return (
    <div data-testid="discharge-register" className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <h2 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-50">
        Discharge Register
      </h2>

      {loading && (
        <div data-testid="register-loading" className="text-sm text-slate-500">Loading discharge register…</div>
      )}

      {!loading && error && (
        <div
          role="alert"
          data-testid="register-error"
          className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100"
        >
          {error}
        </div>
      )}

      {!loading && register && rows.length === 0 && (
        <div className="text-sm text-slate-500">
          No discharges in {periodLabel}{doctorLabelText ? ` for ${doctorLabelText}` : ''}.
        </div>
      )}

      {!loading && register && rows.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table data-testid="register-table" className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <th className="pb-2 pr-3">Patient</th>
                  <th className="pb-2 pr-3">Doctor</th>
                  <th className="pb-2 pr-3">IP No.</th>
                  <th className="pb-2 pr-3">Invoice No.</th>
                  <th className="pb-2 pr-3">Bill No.</th>
                  <th className="pb-2 pr-3">Admitted</th>
                  <th className="pb-2 pr-3">Discharged</th>
                  <th className="pb-2 pr-3">LOS (days)</th>
                  <th className="pb-2 pr-3">Discharge type</th>
                  <th className="pb-2 pr-3">Payer</th>
                  <th className="pb-2 pr-3">Net billed</th>
                  <th className="pb-2 pr-3" title="Already deducted from Net billed — do not deduct again.">Concession</th>
                  <th className="pb-2 pr-3">Deposits adjusted</th>
                  {showPaidAfterDischarge && <th className="pb-2 pr-3">Paid after discharge</th>}
                  <th className="pb-2 pr-3">Insurance settled</th>
                  <th className="pb-2 pr-3">Written off</th>
                  <th className="pb-2 pr-3">Still outstanding</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const money = row.reconciled ? selectRegisterRowMoney(row) : null;
                  const typeLabel = DISCHARGE_TYPE_LABELS[row.discharge_type]
                    || ADMISSION_STATUS_LABELS[row.admission_status]
                    || row.admission_status;
                  // Integration reconcile: `payer_type` is a `PayerType`
                  // member (CASH|TPA|GOVT|CREDIT — the server defaults to
                  // CASH, it is never absent). The payer CLASS is what the
                  // accountant reconciles on, so it always leads; the
                  // insurer/corporate name (`payer_name`, null on a cash
                  // row) is secondary, not a replacement for it. An
                  // unrecognised legacy value renders BLANK — a raw enum
                  // token is never shown to a user.
                  const payerLabel = PAYER_TYPE_LABELS[row.payer_type] || BLANK;
                  const doctorCellLabel = doctorLabel(row.primary_doctor_member_id, row.primary_doctor_name);
                  const backdatedTag = selectBackdatedTag(row);
                  return (
                    <tr
                      key={row.admission_id}
                      data-testid={`register-row-${row.admission_id}`}
                      data-reconciled={row.reconciled ? 'true' : 'false'}
                      className={`border-t border-slate-100 dark:border-slate-800 ${
                        row.reconciled ? '' : 'bg-amber-50 dark:bg-amber-900/20'
                      }`}
                    >
                      <td className="py-1.5 pr-3">
                        {row.patient_name}
                        {!row.reconciled && (
                          <span
                            data-testid="unreconciled-flag"
                            className="ml-2 rounded border border-amber-400 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-200"
                          >
                            Not reconciled
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">{doctorCellLabel}</td>
                      <td className="py-1.5 pr-3">{row.ip_number}</td>
                      <td className="py-1.5 pr-3">{row.invoice_number_printed || BLANK}</td>
                      <td className="py-1.5 pr-3">{row.bill_number_printed || BLANK}</td>
                      <td className="py-1.5 pr-3">{row.admitted_date || BLANK}</td>
                      <td className="py-1.5 pr-3">
                        {row.discharged_date}
                        {backdatedTag && (
                          <div
                            data-testid="backdated-tag"
                            className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300"
                          >
                            {backdatedTag}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">{row.los_days}</td>
                      <td className="py-1.5 pr-3">{typeLabel}</td>
                      <td className="py-1.5 pr-3">
                        {payerLabel}
                        {row.payer_name ? ` · ${row.payer_name}` : ''}
                      </td>
                      <td className="py-1.5 pr-3">{money ? money.netBilled : BLANK}</td>
                      <td className="py-1.5 pr-3">{money ? money.concessions : BLANK}</td>
                      <td className="py-1.5 pr-3">{money ? money.depositsAdjusted : BLANK}</td>
                      {showPaidAfterDischarge && (
                        <td className="py-1.5 pr-3">{money ? money.duesCollected : BLANK}</td>
                      )}
                      <td className="py-1.5 pr-3">{money ? money.insuranceSettled : BLANK}</td>
                      <td className="py-1.5 pr-3">{money ? money.writtenOff : BLANK}</td>
                      <td className="py-1.5 pr-3">{money ? money.stillOutstanding : BLANK}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasUnreconciledRow && (
            <p data-testid="register-unreconciled-footnote" className="mb-2 text-xs text-slate-500 dark:text-slate-400">
              A row marked &quot;Not reconciled&quot; has blank amount cells. Blank means the amount could not
              be worked out. It does not mean ₹0.
            </p>
          )}

          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span data-testid="register-pagination">
              Showing {start + 1}–{end} of {rows.length} discharges
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="register-prev"
                disabled={currentPage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-slate-800 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              >
                Previous
              </button>
              <button
                type="button"
                data-testid="register-next"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-slate-800 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
